import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { Link } from 'react-router'

import ServiceFeeCategory from '@/components/misc/ServiceFeesTable/columns/ServiceFeeCategory'
import ServiceFeeExample from '@/components/misc/ServiceFeesTable/columns/ServiceFeeExample'
import ServiceFeeNotes from '@/components/misc/ServiceFeesTable/columns/ServiceFeeNotes'
import ServiceFeePassBackNotes from '@/components/misc/ServiceFeesTable/columns/ServiceFeePassBackNotes'
import ServiceFeeShareType from '@/components/misc/ServiceFeesTable/columns/ServiceFeeShareType'
import ServiceFeeShareValue from '@/components/misc/ServiceFeesTable/columns/ServiceFeeShareValue'
import { Button } from '@/components/ui/button'
import { TableCell, TableRow } from '@/components/ui/table'
import useAppSelector from '@/hooks/useAppSelector'
import { useIsCompanyUser } from '@/hooks/useIsCompanyUser'
import { useServiceAreas } from '@/hooks/useServiceAreas'
import { faPlus } from '@/lib/fontAwesomePro/regular'
import { getCssVariable } from '@/lib/helpers/domHelpers'
import serviceFeeService from '@/lib/services/serviceFeeService'
import { selectServiceFeeErrors } from '@/redux/slices/commercialAgreementsForm'
import {
  ReferralConsentLetterVariant,
  ReferralConsentStage,
} from '@/types/misc'

export interface ServiceFeesTableRowsProps {
  serviceFees: ServiceFeeDetail[]
  commercialAgreementId?: number
  addAgreementLink?: string
  showExampleColumn: boolean
  renderActionsColumn?: (
    serviceFee: ServiceFeeDetail,
    index: number,
  ) => React.ReactNode
  onRowClick?: (serviceFee: ServiceFeeDetail, index: number) => void
  isRecommendedPartnerTerms?: boolean
  isPreviewLetter: boolean
  referralConsentStage: ReferralConsentStage
  consentLetterVariant: ReferralConsentLetterVariant
}

export interface ServiceFeeDetail {
  serviceArea?: string | null
  serviceAreaOther?: string | null
  feeCategory?: string | null
  feeCategoryOther?: string | null
  shareType?: string | null
  shareValue?: number | null
  duration?: number | string | null
  notes?: string | null
  passBackFee?: boolean | null
  passBackFeeNotes?: string | null
  example?: string | null
  defaultIntroductionExample?: string | null
  defaultRetrospectiveExample?: string | null
  groupIntroductionExample?: string | null
  groupRetrospectiveExample?: string | null
}

const ServiceFeesTableRows: React.FC<ServiceFeesTableRowsProps> = ({
  commercialAgreementId,
  serviceFees,
  addAgreementLink,
  showExampleColumn,
  renderActionsColumn,
  onRowClick,
  isRecommendedPartnerTerms,
  isPreviewLetter,
  consentLetterVariant,
  referralConsentStage,
}) => {
  serviceFeeService.validateServiceFeesOrFail(serviceFees)
  const isCompanyUser = useIsCompanyUser()
  const serviceAreas = useServiceAreas()
  const serviceFeeErrors = useAppSelector(selectServiceFeeErrors)

  const shouldAllowAddingServiceFees =
    !commercialAgreementId && addAgreementLink

  const orderedServiceFees = serviceFeeService.orderServiceFeesByName(
    serviceFees,
    serviceAreas,
  )

  const serviceFeeGroups = Object.entries(
    serviceFeeService.getServiceFeeGroupsForDisplay({
      serviceFees,
      serviceAreas,
    }),
  )

  if (serviceFeeGroups.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={6}>
          <div
            className="flex items-center justify-center"
            style={{ height: addAgreementLink ? '130px' : '30px' }}
          >
            {shouldAllowAddingServiceFees && addAgreementLink ? (
              <Button
                asChild
                variant="success"
                size="lg"
                className="flex items-center gap-2"
              >
                <Link to={addAgreementLink}>
                  <FontAwesomeIcon icon={faPlus} />
                  Add terms
                </Link>
              </Button>
            ) : (
              <p className="mb-4">No commercial terms</p>
            )}
          </div>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      {/* Iterate over service fee groups */}
      {serviceFeeGroups.map((serviceFeeGroup, serviceFeeGroupIndex) => {
        const [serviceAreaName, serviceAreaFees] = serviceFeeGroup

        // Iterate over each service fee within the group
        return serviceAreaFees.map((serviceFee, serviceFeeGroupItemIndex) => {
          const isFirstServiceAreaFee = serviceFeeGroupItemIndex === 0

          const example = serviceFeeService.getExample({
            serviceFee,
            consentLetterVariant,
            referralConsentStage,
          })

          // Note: it's important to use serviceFee.index here to ensure the
          // correct index is passed to any callbacks like `onRowClick`
          // or `renderActionsColumn`
          const serviceFeeIndex = serviceFee.index

          const hasError =
            serviceFeeErrors &&
            Object.keys(serviceFeeErrors)
              .map(Number)
              .includes(serviceFeeIndex + 1)

          return (
            <TableRow
              css={{
                td: {
                  color: hasError ? getCssVariable('--destructive') : undefined,
                },
              }}
              key={`${serviceFeeGroupIndex}[${serviceFeeGroupItemIndex}]`}
              onClick={() => {
                if (onRowClick) {
                  onRowClick(serviceFee, serviceFeeIndex)
                }
              }}
            >
              {/* Service */}
              {isFirstServiceAreaFee && (
                <TableCell
                  className="sticky-column-start align-top font-normal"
                  rowSpan={serviceAreaFees.length}
                >
                  {serviceAreaName}
                </TableCell>
              )}

              {/* Fee category */}
              <TableCell>
                <ServiceFeeCategory serviceFee={serviceFee} />
              </TableCell>

              {/* Share type */}
              {serviceFeeService.shouldShowShareTypeColumn({
                isCompanyUser,
                isPreviewLetter,
              }) && (
                <>
                  <TableCell>
                    <ServiceFeeShareType serviceFee={serviceFee} />
                  </TableCell>
                </>
              )}

              {/* Share value */}
              <TableCell>
                <ServiceFeeShareValue serviceFee={serviceFee} />
              </TableCell>

              {/* Pass back some or all to your client? */}
              {serviceFeeService.shouldShowFeePassBackColumn({
                serviceFees: orderedServiceFees,
                isRecommendedPartnerTerms,
              }) && (
                <TableCell>
                  <ServiceFeePassBackNotes serviceFee={serviceFee} />
                </TableCell>
              )}

              {/* Notes */}
              {serviceFeeService.shouldShowNotesColumn(orderedServiceFees) && (
                <TableCell>
                  <ServiceFeeNotes serviceFee={serviceFee} />
                </TableCell>
              )}

              {/* Example */}
              {showExampleColumn && (
                <TableCell>
                  {example && <ServiceFeeExample example={example} />}
                </TableCell>
              )}

              {renderActionsColumn &&
                renderActionsColumn(serviceFee, serviceFeeIndex)}
            </TableRow>
          )
        })
      })}
    </>
  )
}

export default ServiceFeesTableRows
